import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import enviroment from "../../../enviroments";

const HUB_EVENT_MENSAGEM = "mensagem";
const HUB_EVENT_STATUS = "status";
type WhatsAppHubListener = (payload: any) => void;

let sharedConnection: HubConnection | null = null;
let sharedToken = "";
let startRequested = false;
let startAttempt = 0;
let startRetryTimer: ReturnType<typeof setTimeout> | null = null;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<WhatsAppHubListener>();

const MAX_START_ATTEMPTS = 6;
const START_RETRY_BASE_MS = 2000;
const START_RETRY_MAX_MS = 60_000;

class WhatsAppHubHttpClient extends HttpClient {
  async send(request: HttpRequest): Promise<HttpResponse> {
    if (!request.url) {
      throw new Error("No url defined.");
    }

    const headers = { ...(request.headers || {}) };
    Object.keys(headers).forEach((key) => {
      if (
        key.toLowerCase() === "x-signalr-user-agent" ||
        key.toLowerCase() === "x-requested-with"
      ) {
        delete headers[key];
      }
    });

    const response = await fetch(request.url, {
      body: request.content as BodyInit | undefined,
      cache: "no-cache",
      credentials: request.withCredentials ? "include" : "omit",
      headers,
      method: request.method,
      mode: "cors",
      redirect: "follow",
      signal: request.abortSignal as AbortSignal | undefined,
    });

    const content =
      request.responseType === "arraybuffer"
        ? await response.arrayBuffer()
        : await response.text();

    return new HttpResponse(response.status, response.statusText, content);
  }

  getCookieString() {
    return "";
  }
}

function getHubUrl(token: string): string {
  const base = (enviroment.BASE_URL || "https://prod-sellit.cyrela.com.br")
    .replace(/\s/g, "")
    .replace(/\/$/, "");

  return `${base}/whatsapp/hubs/whatsapp?access_token=${encodeURIComponent(
    token
  )}`;
}

function getSafeHubUrl(): string {
  const base = (enviroment.BASE_URL || "https://prod-sellit.cyrela.com.br")
    .replace(/\s/g, "")
    .replace(/\/$/, "");

  return `${base}/whatsapp/hubs/whatsapp`;
}

function formatHubStartError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function emitToListeners(payload: any) {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {
      // Mantem uma tela com erro isolada das demais assinantes.
    }
  });
}

function shouldStopStartRetry(error: unknown): boolean {
  const message = formatHubStartError(error);

  if (error instanceof Error && error.name === "AbortError") return true;
  if (message.includes("stop()")) return true;

  return (
    message.includes("Handshake was canceled") ||
    message.includes("Server returned handshake error")
  );
}

function closeSharedConnection(resetAttempts = false) {
  startRequested = false;

  if (resetAttempts) {
    startAttempt = 0;
  }

  if (startRetryTimer) {
    clearTimeout(startRetryTimer);
    startRetryTimer = null;
  }

  const conn = sharedConnection;
  sharedConnection = null;
  sharedToken = "";

  if (conn && conn.state !== HubConnectionState.Disconnected) {
    conn.stop().catch(() => undefined);
  }
}

function createSharedConnection(token: string): HubConnection {
  const conn = new HubConnectionBuilder()
    .withUrl(getHubUrl(token), {
      httpClient: new WhatsAppHubHttpClient(),
      withCredentials: false,
      transport: HttpTransportType.WebSockets,
      // Quando forçamos um transporte, o negotiate é desnecessário. Pular evita
      // o loop de "negotiate → upgrade falha → negotiate" e vai direto pro WS.
      skipNegotiation: true,
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.None)
    .build();

  conn.on(HUB_EVENT_MENSAGEM, (payload: any) => {
    emitToListeners(payload);
  });
  conn.on("messages.upsert", (payload: any) => {
    emitToListeners({ tipo: "messages.upsert", data: payload });
  });
  conn.on(HUB_EVENT_STATUS, (payload: any) => {
    emitToListeners({ tipo: HUB_EVENT_STATUS, data: payload });
  });
  conn.on("connection.update", (payload: any) => {
    emitToListeners({ tipo: "connection.update", data: payload });
  });

  // Quando o SignalR desiste (após withAutomaticReconnect esgotar suas tentativas
  // ou o servidor fechar a conexão), garantimos que o nosso estado fique consistente
  // — sem disparar retry automático aqui. Reconexão acontece só quando um novo
  // consumidor monta o hook (e o backoff externo controla o resto).
  conn.onclose(() => {
    if (conn !== sharedConnection) return;
    startRequested = false;
  });

  sharedConnection = conn;
  sharedToken = token;

  return conn;
}

function startSharedConnection(conn: HubConnection) {
  if (startRequested || conn.state !== HubConnectionState.Disconnected) return;
  if (startAttempt >= MAX_START_ATTEMPTS) return;

  startRequested = true;
  startAttempt += 1;

  const tryStart = async (): Promise<void> => {
    try {
      await conn.start();
      startAttempt = 0;
      if (conn.state !== HubConnectionState.Connected) {
        console.warn("[WhatsAppHub] SignalR iniciou sem estado conectado", {
          state: conn.state,
          url: getSafeHubUrl(),
        });
      }
    } catch (error) {
      startRequested = false;

      if (conn !== sharedConnection || listeners.size === 0) return;

      const message = formatHubStartError(error);
      if (shouldStopStartRetry(error)) {
        console.warn(`[WhatsAppHub] SignalR nao iniciou: ${message}`);
        return;
      }

      console.error("[WhatsAppHub] Erro ao iniciar SignalR", {
        url: getSafeHubUrl(),
        attempt: startAttempt,
        error,
      });

      if (startAttempt >= MAX_START_ATTEMPTS) {
        console.warn(
          `[WhatsAppHub] Limite de tentativas atingido (${MAX_START_ATTEMPTS}). Abortando.`
        );
        return;
      }

      const backoff = Math.min(
        START_RETRY_BASE_MS * 2 ** (startAttempt - 1),
        START_RETRY_MAX_MS
      );

      if (startRetryTimer) clearTimeout(startRetryTimer);
      startRetryTimer = setTimeout(() => {
        startRetryTimer = null;
        if (conn === sharedConnection && listeners.size > 0) {
          startSharedConnection(conn);
        }
      }, backoff);
    }
  };

  tryStart();
}

function ensureSharedConnection(token: string): HubConnection {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  if (sharedConnection && sharedToken !== token) {
    // Token de fato mudou (login novo / refresh). Reseta contagem.
    closeSharedConnection(true);
  }

  const conn = sharedConnection ?? createSharedConnection(token);
  startSharedConnection(conn);

  return conn;
}

export function useWhatsAppHub(
  token: string,
  onMensagem: (payload: any) => void
) {
  const connRef = useRef<HubConnection | null>(null);
  const onMensagemRef = useRef(onMensagem);

  useEffect(() => {
    onMensagemRef.current = onMensagem;
  }, [onMensagem]);

  useEffect(() => {
    if (!token) return undefined;

    const listener: WhatsAppHubListener = (payload) => {
      onMensagemRef.current(payload);
    };

    listeners.add(listener);
    connRef.current = ensureSharedConnection(token);

    return () => {
      listeners.delete(listener);

      if (listeners.size === 0) {
        // Aguarda 10s antes de desconectar. Evita churn quando o caller
        // recria o effect rapidamente (ex.: useWhatsAppStatus oscilando
        // conectado true/false, ou re-render que muda o token efetivo).
        stopTimer = setTimeout(() => {
          if (listeners.size === 0) {
            closeSharedConnection();
          }
        }, 10_000);
      }
    };
  }, [token]);

  return connRef;
}
